import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { BadStatusError, cfetch, containsNetworkError } from '@confluence/network';
import { getLogger } from '@confluence/logger';

// Console diagnostics for local and dogfooding debugging. Opt in per session with
// sessionStorage.setItem('confluence.enable-logging', 'true') — silent for everyone else, and
// a no-op under NODE_ENV=testing.
//
// Never log identifiers. Not prompt content, and not contentId or agentIdentityAccountId either:
// the console is copied into tickets and screenshots, so keep it to the categorical shape of what
// happened — reason and status code. The identifiers are already visible in the network panel to
// anyone debugging a specific request.
const logger = getLogger('recordAgenticView');

// Served by conversational-ai-platform (ConfluenceAgenticViewV1Controller). Convo AI makes
// the ASAP S2S hop to Confluence's /wiki/api/v2/pages/{id}/agentic/view endpoint; the browser
// cannot call that endpoint directly.
const AGENTIC_VIEW_URL = '/gateway/api/assist/api/confluence/v1/agentic-views';

export type RecordAgenticViewPayload = {
	contentId: string;
	pageContext: string;
	agentIdentityAccountId?: string;
};

export type RecordAgenticViewTelemetryAttributes = {
	trigger?: 'inlineCommentMention';
};

/**
 * Categorical reason attached to an `agenticViewPresence failed` operational event, so that
 * failures group by cause rather than by free text.
 *
 * The raw error message is deliberately never emitted: it can echo request content, and
 * analytics is not a place for user content. Only this reason and a numeric status code go out.
 */
export enum AgenticViewFailureReason {
	/** 400 — the request violated the service contract. A bug on our side, not a service fault. */
	BAD_REQUEST = 'badRequest',
	/** 401 / 403 — the user may not record presence for this page. */
	FORBIDDEN = 'forbidden',
	/**
	 * 404 — no route matched. Until CEPS-13034 adds this path to the Stargate service policy
	 * allowlist, *every* call fails this way; afterwards it means the page no longer exists.
	 * Expect this reason to dominate until that ticket lands.
	 */
	NOT_FOUND = 'notFound',
	/** 429 — Stargate or Convo AI is shedding load. */
	RATE_LIMITED = 'rateLimited',
	/** 5xx — Convo AI or NCS is unhealthy. */
	SERVER_ERROR = 'serverError',
	/** No response at all — the user's connectivity, not a service fault. */
	NETWORK = 'network',
	/** Anything else, including a non-Error throw. */
	UNKNOWN = 'unknown',
}

type AgenticViewFailure = {
	reason: AgenticViewFailureReason;
	statusCode?: number;
};

/**
 * Emits one operational event, absorbing anything it throws. Reporting is best-effort: the
 * call site voids this client's promise, so a throw while reporting would surface as an
 * unhandled rejection rather than as a report.
 */
const fireOperationalEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	action: 'attempted' | 'failed',
	attributes?: Record<string, unknown>,
): void => {
	try {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action,
				actionSubject: 'agenticViewPresence',
				source: 'AIMate',
				tags: ['aiMate'],
				...(attributes && { attributes }),
			},
		}).fire();
	} catch {
		// Intentionally swallowed — see above.
	}
};

/**
 * The status codes that have a dedicated reason, as data. 5xx is deliberately absent: it is a
 * range rather than a set of codes, so it stays a comparison in `reasonForStatusCode`.
 *
 * `Partial<Record<…>>` rather than `Record<…>` on purpose. Confluence does not enable
 * `noUncheckedIndexedAccess`, so a total `Record` would type every lookup as already resolved and
 * make the fallback below read as unreachable — when in fact it is what handles every 5xx.
 */
const REASON_BY_STATUS_CODE: Partial<Record<number, AgenticViewFailureReason>> = {
	400: AgenticViewFailureReason.BAD_REQUEST,
	// One reason for two codes: whether the caller is unauthenticated or merely unauthorised is not
	// a distinction worth its own failure bucket here.
	401: AgenticViewFailureReason.FORBIDDEN,
	403: AgenticViewFailureReason.FORBIDDEN,
	404: AgenticViewFailureReason.NOT_FOUND,
	// Reached by RateLimitedError as well as a plain BadStatusError — see `classifyFailure`.
	429: AgenticViewFailureReason.RATE_LIMITED,
};

const reasonForStatusCode = (statusCode: number): AgenticViewFailureReason =>
	REASON_BY_STATUS_CODE[statusCode] ??
	(statusCode >= 500 ? AgenticViewFailureReason.SERVER_ERROR : AgenticViewFailureReason.UNKNOWN);

const classifyFailure = (error: unknown): AgenticViewFailure => {
	// Checked before the generic network case because BadStatusError extends NetworkError.
	// RateLimitedError extends BadStatusError and does not override `name`, so 429 lands here too.
	if (BadStatusError.isBadStatusError(error)) {
		const statusCode = error.response.status;
		return { reason: reasonForStatusCode(statusCode), statusCode };
	}

	if (error instanceof Error && containsNetworkError(error)) {
		return { reason: AgenticViewFailureReason.NETWORK };
	}

	return { reason: AgenticViewFailureReason.UNKNOWN };
};

/**
 * Records that an agent "viewed" a page, as a side effect of a prompt sent from Agent Chat
 * with page context. Fire-and-forget: never awaited by the caller, and no failure may surface
 * to the user or affect delivery of the chat prompt.
 *
 * The service returns 204 on success — and also 204 when its own CONFLUENCE_AGENTIC_VIEW_PRESENCE
 * gate is off, so a 2xx is not evidence that presence was recorded. There is therefore no success
 * event: it would assert something this client cannot know.
 *
 * Two operational events are emitted, forming the tail of a three-step funnel:
 *
 *   aiMate onPromptSentInvoked      platform — the callback fired at all
 *   agenticViewPresence attempted   here — this package's guards and experiment let it through
 *   agenticViewPresence failed      here — with a categorical reason and status code
 *
 * Step 1 minus step 2 is what the guards and the experiment filtered out; step 2 is the
 * denominator for the failure rate. Both of the events emitted here fire only inside the
 * experiment-gated path, so with the experiment off no new event is produced.
 *
 * Failures are reported twice, deliberately. `submitError` gives on-call a stack trace and a
 * trace ID, but because BadStatusError extends NetworkError the monitoring client marks it
 * ignorable-by-on-call (see CustomMonitoringClient's isErrorIgnorableByOnCall), and the jsErrors
 * counter carries no status dimension. The operational event is what makes the failure rate
 * queryable and sliceable by cause.
 *
 * Only page context identifiers are transmitted. Never pass prompt content here.
 */
export const recordAgenticView = async (
	payload: RecordAgenticViewPayload,
	cloudId: string,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	telemetryAttributes?: RecordAgenticViewTelemetryAttributes,
): Promise<void> => {
	// The denominator. Emitted before the request rather than after a 2xx, because a 204 cannot
	// distinguish "presence recorded" from "Convo AI's own gate is off" — an `attempted` count is
	// something this client can honestly assert, a success count is not. Paired with `failed`
	// it yields a failure rate; compared against platform's `aiMate onPromptSentInvoked` it
	// shows how many invocations this package's guards and experiment filtered out.
	fireOperationalEvent(createAnalyticsEvent, 'attempted', telemetryAttributes);

	logger.debug`Recording agent view presence for pageContext=${payload.pageContext}`;

	try {
		await cfetch(AGENTIC_VIEW_URL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
				'X-Product': 'confluence',
				// Not free text. Convo AI resolves this against a closed enum (`Experience.kt` in
				// conversational-ai-platform) in a request filter, before the controller deserialises
				// the body; an unregistered id throws `ExperienceIdNotFoundException`, which extends
				// `BadRequestException`, so it 400s. A descriptive
				// `confluence-agent-chat-view-presence` was rejected exactly that way.
				//
				// `ai-mate` is registered for all products and describes this surface accurately: the
				// write is a side effect of a Rovo Chat prompt. The cost is attribution — these calls
				// are indistinguishable from the rest of Rovo Chat in experience-level metrics, so the
				// `agenticViewPresence` operational events above are how this path gets measured.
				// A dedicated experience needs a conversational-ai-platform change: an `Experience`
				// entry plus a branch in `FeatureConfigServiceImpl`, whose `when` is exhaustive.
				'X-Experience-Id': 'ai-mate',
				'X-Cloudid': cloudId,
			},
			body: JSON.stringify(payload),
			// Presence is a background side effect. Without this, a genuine connectivity failure
			// flips the global connection status to OFFLINE via the cfetch subscriber in
			// graphql/src/createClient.ts, showing an offline indicator for something the user
			// never initiated. Non-2xx responses take a different path and cannot do this.
			ignoreNoNetworkError: true,
		});

		// Not "recorded" — the service also 204s when its own gate is off. See above.
		logger.debug`Request accepted. A 2xx does not confirm presence was recorded: Convo AI returns 204 with CONFLUENCE_AGENTIC_VIEW_PRESENCE off too.`;
	} catch (error) {
		const { reason, statusCode } = classifyFailure(error);

		// Mirrors the operational event exactly, and deliberately nothing more. The raw error
		// object is not logged: it carries the Response, and the full detail plus a stack trace
		// already reaches on-call through submitError below.
		logger.error`Failed to record agent view presence: reason=${reason} statusCode=${statusCode}`;

		fireOperationalEvent(createAnalyticsEvent, 'failed', {
			...telemetryAttributes,
			reason,
			...(statusCode !== undefined && { statusCode }),
		});

		getMonitoringClient().submitError(error, { attribution: Attribution.AI_MATE });
	}
};
